import { useState, useEffect } from 'react'

export const useAchievements = () => {
  // 初始化成就狀態
  const [achievements, setAchievements] = useState(() => {
    try {
      const saved = localStorage.getItem('beat-buddy-achievements')
      if (saved) {
        return JSON.parse(saved)
      }
    } catch (error) {
      console.error('Error loading achievements:', error)
    }
    // 默認狀態
    return {
      firstStart: true,
      fiveMinutes: false,
      tenMinutes: false,
      twentyMinutes: false,
      thirtyMinutes: false,
      sixtyMinutes: false,
      hundredTwentyMinutes: false,
      changedTempo: false,
      changedTimeSignature: false,
      changedCharacter: false,
      tempoMaster: false,
      accessoriesMaster: false,
      speedDemon: false,
      slowAndSteady: false,
      complexRhythm: false
    }
  })

  // 追蹤數據
  const [triedTempos, setTriedTempos] = useState(() => {
    try {
      const saved = localStorage.getItem('beat-buddy-tried-tempos')
      if (saved) {
        return JSON.parse(saved)
      }
    } catch (error) {
      console.error('Error loading tried tempos:', error)
    }
    return [100] // 默認包含初始速度
  })

  const [triedTimeSigs, setTriedTimeSigs] = useState(() => {
    try {
      const saved = localStorage.getItem('beat-buddy-tried-timesigs')
      if (saved) {
        return JSON.parse(saved)
      }
    } catch (error) {
      console.error('Error loading tried time signatures:', error)
    }
    return [4] // 默認包含初始拍號
  })

  const [triedAccessories, setTriedAccessories] = useState(() => {
    try {
      const saved = localStorage.getItem('beat-buddy-tried-accessories')
      if (saved) {
        return JSON.parse(saved)
      }
    } catch (error) {
      console.error('Error loading tried accessories:', error)
    }
    return [] // 配件默認為空
  })

  // 成就彈窗狀態
  const [achievementPopup, setAchievementPopup] = useState(null)

  // 保存數據到 localStorage
  useEffect(() => {
    try {
      localStorage.setItem('beat-buddy-achievements', JSON.stringify(achievements))
    } catch (error) {
      console.error('Error saving achievements:', error)
    }
  }, [achievements])

  useEffect(() => {
    try {
      localStorage.setItem('beat-buddy-tried-tempos', JSON.stringify(triedTempos))
    } catch (error) {
      console.error('Error saving tried tempos:', error)
    }
  }, [triedTempos])

  useEffect(() => {
    try {
      localStorage.setItem('beat-buddy-tried-timesigs', JSON.stringify(triedTimeSigs))
    } catch (error) {
      console.error('Error saving tried time signatures:', error)
    }
  }, [triedTimeSigs])

  useEffect(() => {
    try {
      localStorage.setItem('beat-buddy-tried-accessories', JSON.stringify(triedAccessories))
    } catch (error) {
      console.error('Error saving tried accessories:', error)
    }
  }, [triedAccessories])

  // 解鎖成就的核心函數
  const unlockAchievement = (achievementId, title, description, emoji) => {
    console.log(`🏆 Unlocking achievement: ${achievementId}`)
    setAchievements(prev => {
      if (prev[achievementId]) {
        console.log(`Achievement ${achievementId} already unlocked`)
        return prev
      }
      const newAchievements = { ...prev, [achievementId]: true }
      console.log(`✅ Achievement ${achievementId} unlocked successfully`)

      // 顯示彈窗
      setAchievementPopup({
        title,
        description,
        emoji
      })
      return newAchievements
    })
  }

  // 檢查時間成就並返回解鎖的角色
  const checkTimeAchievements = (totalTime, lastCheckedTime = 0) => {
    console.log(`⏰ Checking time achievements: ${lastCheckedTime} → ${totalTime} seconds`)
    const unlocks = []

    // 定義時間門檻
    const timeThresholds = [
      { seconds: 300, id: 'fiveMinutes', title: '節奏小精靈！', description: '你持續練習了5分鐘！', emoji: '⏱️' },
      { seconds: 600, id: 'tenMinutes', title: '節拍探險家！', description: '你持續練習了10分鐘！', emoji: '⌛', unlock: 'dragon' },
      { seconds: 1200, id: 'twentyMinutes', title: '節奏守護者！', description: '你持續練習了20分鐘！', emoji: '🕐', unlock: 'panda' },
      { seconds: 1800, id: 'thirtyMinutes', title: '節拍魔法師！', description: '你持續練習了30分鐘！', emoji: '🕑' },
      { seconds: 3600, id: 'sixtyMinutes', title: '節奏大師！', description: '你持續練習了1小時！', emoji: '🏆', unlock: 'robot' },
      { seconds: 7200, id: 'hundredTwentyMinutes', title: '音樂傳奇！', description: '你持續練習了2小時！', emoji: '👑', unlock: 'wizard' }
    ]

    // 檢查每個門檻
    timeThresholds.forEach(threshold => {
      // 只有當：1. 總時間達到門檻 2. 上次檢查時間未達到門檻 3. 成就未解鎖
      if (totalTime >= threshold.seconds && lastCheckedTime < threshold.seconds && !achievements[threshold.id]) {
        console.log(`🎉 Unlocking ${threshold.id} at ${totalTime}s (was ${lastCheckedTime}s)`)
        unlockAchievement(threshold.id, threshold.title, threshold.description, threshold.emoji)
        if (threshold.unlock) {
          unlocks.push(threshold.unlock)
        }
      }
    })

    return unlocks
  }

  // 檢查速度成就
  const checkTempoAchievements = (tempo) => {
    console.log(`🎵 Checking tempo ${tempo}, tried: ${triedTempos.join(',')}`)

    // 記錄新速度
    if (!triedTempos.includes(tempo)) {
      setTriedTempos(prev => [...prev, tempo])
    }

    // 首次改變速度
    if (tempo !== 100 && !achievements.changedTempo) {
      unlockAchievement('changedTempo', '速度冒險家！', '你改變了節拍速度！', '🚀')
    }

    // 極速成就 (200+ BPM)
    if (tempo >= 200 && !achievements.speedDemon) {
      unlockAchievement('speedDemon', '速度惡魔！', '使用200BPM以上的速度！', '⚡')
    }

    // 慢速成就 (60- BPM)
    if (tempo <= 60 && !achievements.slowAndSteady) {
      unlockAchievement('slowAndSteady', '慢工出細活！', '使用60BPM以下的速度！', '🐌')
    }

    // 檢查是否應該解鎖狐狸 (5種不同速度)
    const currentTriedTempos = triedTempos.includes(tempo) ? triedTempos : [...triedTempos, tempo]
    if (currentTriedTempos.length >= 5 && !achievements.tempoMaster) {
      unlockAchievement('tempoMaster', '速度收藏家！', '你嘗試了5種不同的速度！', '🎼')
      return { shouldUnlockFox: true }
    }
    return { shouldUnlockFox: false }
  }

  // 檢查拍號成就
  const checkTimeSigAchievements = (beatsPerMeasure, unlockedCharacters = []) => {
    console.log(`📊 Checking time sig ${beatsPerMeasure}, tried: ${triedTimeSigs.join(',')}`)

    // 記錄新拍號
    if (!triedTimeSigs.includes(beatsPerMeasure)) {
      setTriedTimeSigs(prev => [...prev, beatsPerMeasure])
    }

    // 首次改變拍號
    if (beatsPerMeasure !== 4 && !achievements.changedTimeSignature) {
      unlockAchievement('changedTimeSignature', '拍號魔術師！', '你嘗試了不同的拍號！', '🔄')
    }

    // 複雜節奏成就 (5拍以上)
    if (beatsPerMeasure >= 5 && !achievements.complexRhythm) {
      unlockAchievement('complexRhythm', '複雜節奏大師！', '使用5拍以上的拍號！', '🎯')
    }

    // 檢查是否應該解鎖忍者 (試過所有拍號 1-6)
    const currentTriedTimeSigs = triedTimeSigs.includes(beatsPerMeasure) ? triedTimeSigs : [...triedTimeSigs, beatsPerMeasure]
    console.log(`🥷 Ninja check: tried ${currentTriedTimeSigs.length} time signatures: [${currentTriedTimeSigs.sort().join(',')}]`)
    console.log(`🥷 Ninja already unlocked: ${unlockedCharacters.includes('ninja')}`)

    // 檢查是否已經試過所有6種拍號 (1,2,3,4,5,6)
    const allTimeSigs = [1, 2, 3, 4, 5, 6]
    const hasAllTimeSigs = allTimeSigs.every(sig => currentTriedTimeSigs.includes(sig))

    // 關鍵修正：只有當忍者未解鎖時才返回 true
    if (hasAllTimeSigs && !unlockedCharacters.includes('ninja')) {
      console.log('🎯 All time signatures tried and ninja not unlocked! Should unlock ninja')
      return { shouldUnlockNinja: true }
    }
    return { shouldUnlockNinja: false }
  }

  // 檢查配件成就
  const checkAccessoryAchievements = (accessory) => {
    if (accessory === 'none') {
      return { shouldUnlockAlien: false }
    }
    
    console.log(`👗 Checking accessory ${accessory}, tried: ${triedAccessories.join(',')}`)

    // 記錄新配件
    if (!triedAccessories.includes(accessory)) {
      setTriedAccessories(prev => [...prev, accessory])
    }

    // 檢查是否應該解鎖外星人 (試過所有配件 7種)
    const currentTriedAccessories = triedAccessories.includes(accessory) ? triedAccessories : [...triedAccessories, accessory]
    if (currentTriedAccessories.length >= 7 && !achievements.accessoriesMaster) {
      unlockAchievement('accessoriesMaster', '時尚設計師！', '你嘗試了所有配件！', '✨')
      return { shouldUnlockAlien: true }
    }
    return { shouldUnlockAlien: false }
  }

  // 檢查角色自訂成就
  const checkCharacterAchievements = () => {
    console.log('🎭 Checking character change achievement')
    if (!achievements.changedCharacter) {
      console.log('🎭 Unlocking creative designer achievement')
      unlockAchievement('changedCharacter', '創意設計師！', '你自訂了自己的角色！', '🎭')
    } else {
      console.log('🎭 Creative designer achievement already unlocked')
    }
  }

  // 檢查公主解鎖條件
  const checkPrincessUnlock = (unlockedCharacters) => {
    const requiredCharacters = ['unicorn', 'dragon', 'panda', 'fox', 'alien', 'robot', 'wizard', 'ninja']
    const hasAllRequired = requiredCharacters.every(char => unlockedCharacters.includes(char))
    const shouldUnlock = hasAllRequired && !unlockedCharacters.includes('princess')
    console.log(`👸 Princess check: ${hasAllRequired ? 'YES' : 'NO'} (${unlockedCharacters.length}/8)`)
    return shouldUnlock
  }

  // 檢查超級英雄解鎖條件
  const checkSuperheroUnlock = (unlockedCharacters) => {
    const allCharacters = ['unicorn', 'dragon', 'panda', 'fox', 'alien', 'robot', 'wizard', 'ninja', 'princess']
    const hasAllCharacters = allCharacters.every(char => unlockedCharacters.includes(char))
    
    const mainAchievements = [
      'fiveMinutes', 'tenMinutes', 'twentyMinutes', 'thirtyMinutes', 'sixtyMinutes', 
      'hundredTwentyMinutes', 'changedTempo', 'changedTimeSignature', 'changedCharacter', 
      'tempoMaster', 'accessoriesMaster', 'speedDemon', 'slowAndSteady', 'complexRhythm'
    ]
    
    const hasAllAchievements = mainAchievements.every(ach => achievements[ach])
    
    const shouldUnlock = hasAllCharacters && hasAllAchievements && !unlockedCharacters.includes('superhero')
    
    console.log(`🦸 Superhero check: chars=${hasAllCharacters} achievements=${hasAllAchievements}`)
    return shouldUnlock
  }

  // 隱藏成就彈窗
  const hideAchievementPopup = () => {
    setAchievementPopup(null)
  }

  return {
    achievements,
    checkTimeAchievements,
    checkTempoAchievements,
    checkTimeSigAchievements,
    checkAccessoryAchievements,
    checkCharacterAchievements,
    checkPrincessUnlock,
    checkSuperheroUnlock,
    achievementPopup,
    hideAchievementPopup,
    triedTempos,
    triedTimeSigs,
    triedAccessories
  }
}